import { ProcedureButton } from "@/features/procedures/ui/components/common/ProcedureButton";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import { Box, IconButton, alpha } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { components } from "@reactour/tour";
import { Translate, GetLanguageValueAsString } from "@/app/application/Translate";
import { useContext } from "react";

import { Language } from "@/model/Language/Language";

import { TranslationContext } from "@/app/providers";



const { Badge: BaseBadge } = components;

export const TourClose = (props: {
	onClick?: () => void;
	disabled?: boolean;
}) => {
	const translation: Language | null = useContext(TranslationContext);
	const theme = useTheme();

	return (
		<IconButton
			aria-label={GetLanguageValueAsString(translation, "Auto.App.Application.Tour.TourComponents.L21C15.CloseTour", "Close tour")}
			onClick={props.onClick}
			disabled={props.disabled}
			size="small"
			sx={{
				position: "absolute",
				top: 12,
				right: 12,
				width: 36,
				height: 36,
				flexShrink: 0,
				bgcolor: alpha(theme.palette.background.paper, 0.8),
				border: `1px solid ${theme.palette.divider}`,
				color: "text.secondary",
				"&:hover": {
					bgcolor: alpha(theme.palette.primary.main, 0.12),
					borderColor: theme.palette.primary.main,
					color: "primary.main",
				},
			}}
		>
			<CloseIcon fontSize="small" />
		</IconButton>
	);
};

export const TourBadge = (props: {
	children?: React.ReactNode;
	styles?: Record<string, unknown>;
}) => {
	const theme = useTheme();

	return (
		<BaseBadge {...props}>
			<Box
				component="span"
				sx={{
					display: "inline-flex",
					alignItems: "center",
					justifyContent: "center",
					minWidth: 44,
					height: 28,
					px: 1.5,
					borderRadius: 999,
					bgcolor: theme.palette.primary.main,
					color: theme.palette.primary.contrastText,
					fontWeight: 800,
					fontSize: 13,
					letterSpacing: "-0.02em",
				}}
			>
				{props.children}
			</Box>
		</BaseBadge>
	);
};

/** Custom next button for tour – app-aligned styling */
export const TourNextButton = (props: {
	Button: React.FC<{
		onClick?: () => void;
		kind?: string;
		hideArrow?: boolean;
	}>;
	setCurrentStep: (fn: (n: number) => number) => void;
	stepsLength: number;
	currentStep: number;
	setIsOpen: (open: boolean) => void;
}) => {
	const { setCurrentStep, stepsLength, currentStep, setIsOpen } = props;
	const isLastStep = currentStep === stepsLength - 1;

	if (isLastStep) {
		return (
			<ProcedureButton
				intent="primary"
				size="small"
				onClick={() => setIsOpen(false)}
			>
				<Translate String="Auto.App.Application.Tour.TourComponents.L97C5.Done">Done</Translate>
			</ProcedureButton>
		);
	}

	return (
		<ProcedureButton
			intent="primary"
			size="small"
			endIcon={<ChevronRightIcon sx={{ fontSize: 18 }} />}
			onClick={() => setCurrentStep((s) => Math.min(s + 1, stepsLength - 1))}
		>
			<Translate String="Auto.App.Application.Tour.TourComponents.L109C4.Next">Next</Translate>
		</ProcedureButton>
	);
};

/** Custom prev button for tour – app-aligned styling */
export const TourPrevButton = (props: {
	Button: React.FC<{
		onClick?: () => void;
		kind?: string;
		hideArrow?: boolean;
	}>;
	setCurrentStep: (fn: (n: number) => number) => void;
	stepsLength: number;
	currentStep: number;
}) => {
	const { setCurrentStep, currentStep } = props;
	const isFirstStep = currentStep === 0;

	if (isFirstStep) return null;

	return (
		<ProcedureButton
			intent="ghost"
			size="small"
			startIcon={<ChevronLeftIcon sx={{ fontSize: 18 }} />}
			onClick={() => setCurrentStep((s) => Math.max(s - 1, 0))}
			sx={{ color: "text.secondary" }}
		>
			<Translate String="Auto.App.Application.Tour.TourComponents.L138C4.Back">Back</Translate>
		</ProcedureButton>
	);
};
